import React from 'react';
import type { CampaignInvestmentRow } from '../../../utils/creditPricing';

/**
 * InvestedSplitChip — the campaign INVESTED panel (Manuel): every category
 * (Generated / Enriched / Outreached) AND the total are directly visible —
 * no hover required. One horizontal strip of stat cells (tiny label above a
 * bold number, the same pattern as the Team hero substats), separated by
 * hairlines, with the TOTAL cell visually anchored on the right. Hover is
 * reduced to native `title` hints carrying the per-unit detail
 * (count × unit). ONE component for every card variant so the numbers can
 * never drift between grid/list/compact/detail.
 */

const fmt = (amount: number): string =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

const SIZES = {
  sm: { label: 'text-[8px]', value: 'text-[12px]', totalValue: 'text-[15px]', pad: 'px-3 py-1.5' },
  md: { label: 'text-[9px]', value: 'text-[13px]', totalValue: 'text-[17px]', pad: 'px-3.5 py-2' },
} as const;

export const InvestedSplitChip: React.FC<{
  rows: CampaignInvestmentRow[];
  total: number;
  /** Uploaded-CSV campaigns skip generation — hinted on the Generated cell. */
  leadGenerationApplied?: boolean;
  size?: 'sm' | 'md';
}> = ({ rows, total, leadGenerationApplied = true, size = 'sm' }) => {
  const s = SIZES[size];

  const rowHint = (row: CampaignInvestmentRow): string | undefined => {
    if (row.total === 0 && row.count === 0) {
      if (row.key === 'outreach') return 'No outreach credits — sales intelligence module not active';
      return undefined;
    }
    const base = `${row.label}: ${row.count.toLocaleString()} × ${fmt(row.unit)} credits`;
    if (row.key === 'generated' && !leadGenerationApplied) {
      return `${base} · uploaded list, no generation cost`;
    }
    return base;
  };

  return (
    <div
      data-invested-chip
      className="inline-flex items-stretch overflow-hidden rounded-2xl border border-white/8 bg-white/[0.025] transition-colors hover:border-white/14"
      aria-label={`Invested credits: ${rows.map((row) => `${row.label} ${fmt(row.total)}`).join(', ')}. Total ${fmt(total)} credits.`}
    >
      {/* Leading label cell */}
      <div className={`flex flex-col justify-center gap-1 border-r border-white/8 bg-white/[0.02] ${s.pad}`}>
        <span className={`${s.label} font-black uppercase leading-none tracking-[0.16em] text-[#6E748C]`}>Invested</span>
        <span className="text-[8px] font-semibold leading-none text-[#525878]">credits</span>
      </div>
      {/* Category cells — value always visible; a zero category (e.g. no
          sales module) mutes the WHOLE cell so it reads as "not applicable"
          rather than "spent 0". */}
      {rows.map((row) => {
        const isZero = row.total === 0;
        return (
          <div
            key={row.key}
            title={rowHint(row)}
            className={`flex flex-col justify-center gap-1 border-r border-white/8 ${s.pad}`}
          >
            <span className={`${s.label} flex items-center gap-1.5 font-black uppercase leading-none tracking-[0.12em] ${isZero ? 'text-white/28' : row.tone}`}>
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: 'currentColor', opacity: isZero ? 0.4 : 1 }}
                aria-hidden="true"
              />
              {row.label}
            </span>
            <span className={`${s.value} font-black leading-none tabular-nums ${isZero ? 'text-white/30' : 'text-white'}`}>
              {fmt(row.total)}
            </span>
          </div>
        );
      })}
      {/* Total cell — anchored right, slightly emphasized */}
      <div className={`flex flex-col justify-center gap-1 bg-white/[0.035] ${s.pad}`}>
        <span className={`${s.label} font-black uppercase leading-none tracking-[0.16em] text-white/50`}>Total</span>
        <span className={`${s.totalValue} font-black leading-none text-white tabular-nums`}>{fmt(total)}</span>
      </div>
    </div>
  );
};
